import React, { useState } from 'react';
import { ArrowUpRight, ArrowDownRight, PlusCircle, Trash2 } from 'lucide-react';
import Button from '../../../../../components/ui/Button';

/**
 * Компонент настроек денежных потоков (дополнительные инвестиции и снятия)
 */
const CashFlowSection = ({ 
  formData, 
  handleInputChange, 
  errors 
}) => {
  const [newInvestment, setNewInvestment] = useState({ month: '', amount: '' });
  const [newWithdrawal, setNewWithdrawal] = useState({ month: '', amount: '' });

  // Обработчик добавления дополнительной инвестиции
  const handleAddInvestment = () => {
    if (!newInvestment.month || !newInvestment.amount) return;
    
    const month = parseInt(newInvestment.month, 10);
    const amount = parseFloat(newInvestment.amount.replace(/,/g, '.'));
    
    if (isNaN(month) || month <= 0 || isNaN(amount) || amount <= 0) return;
    
    const updatedInvestments = [
      ...(formData.additionalInvestments || []),
      { month, amount }
    ].sort((a, b) => a.month - b.month);
    
    // Обновление состояния формы
    const newFormData = {
      ...formData,
      additionalInvestments: updatedInvestments
    };
    
    // Эмулируем событие изменения для родительского компонента
    handleInputChange({
      target: {
        name: 'additionalInvestments',
        value: updatedInvestments
      }
    });
    
    // Сбросить форму добавления
    setNewInvestment({ month: '', amount: '' });
  };

  // Обработчик добавления снятия
  const handleAddWithdrawal = () => {
    if (!newWithdrawal.month || !newWithdrawal.amount) return;
    
    const month = parseInt(newWithdrawal.month, 10);
    const amount = parseFloat(newWithdrawal.amount.replace(/,/g, '.'));
    
    if (isNaN(month) || month <= 0 || isNaN(amount) || amount <= 0) return;
    
    const updatedWithdrawals = [
      ...(formData.withdrawals || []),
      { month, amount }
    ].sort((a, b) => a.month - b.month);
    
    // Обновление состояния формы
    handleInputChange({
      target: {
        name: 'withdrawals',
        value: updatedWithdrawals
      }
    });
    
    // Сбросить форму добавления
    setNewWithdrawal({ month: '', amount: '' });
  };

  // Обработчик удаления инвестиции
  const handleRemoveInvestment = (index) => {
    const updatedInvestments = [...formData.additionalInvestments];
    updatedInvestments.splice(index, 1);
    
    handleInputChange({
      target: {
        name: 'additionalInvestments',
        value: updatedInvestments
      }
    });
  };

  // Обработчик удаления снятия
  const handleRemoveWithdrawal = (index) => {
    const updatedWithdrawals = [...formData.withdrawals];
    updatedWithdrawals.splice(index, 1);
    
    handleInputChange({
      target: {
        name: 'withdrawals',
        value: updatedWithdrawals
      }
    });
  };

  return (
    <div className={`p-4 rounded-lg border ${formData.includeCashFlow ? 'bg-purple-50 dark:bg-purple-900/20 border-purple-100 dark:border-purple-800' : 'bg-gray-50 dark:bg-gray-800/50 border-gray-200 dark:border-gray-700'}`}>
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-base font-medium text-gray-900 dark:text-white flex items-center">
          <ArrowUpRight className="w-5 h-5 mr-2 text-purple-500" />
          Дополнительные денежные потоки
        </h3>
        <label className="inline-flex items-center">
          <input
            type="checkbox"
            name="includeCashFlow"
            checked={formData.includeCashFlow}
            onChange={handleInputChange}
            className="form-checkbox"
          />
          <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
            Включить потоки
          </span>
        </label>
      </div>

      {formData.includeCashFlow && (
        <div className="space-y-4">
          {/* Дополнительные инвестиции */}
          <div>
            <h4 className="text-sm font-medium text-gray-800 dark:text-gray-200 mb-2 flex items-center">
              <ArrowUpRight className="w-4 h-4 mr-1 text-green-500" />
              Дополнительные инвестиции
            </h4>

            <div className="grid grid-cols-12 gap-3 mb-2">
              <div className="col-span-4">
                <label htmlFor="investmentMonth" className="block text-xs text-gray-500 dark:text-gray-400 mb-1">
                  Месяц
                </label>
                <input
                  type="text"
                  id="investmentMonth"
                  value={newInvestment.month}
                  onChange={(e) => setNewInvestment({ ...newInvestment, month: e.target.value })}
                  className="form-input text-sm w-full"
                  placeholder="Номер месяца"
                />
              </div>
              <div className="col-span-6">
                <label htmlFor="investmentAmount" className="block text-xs text-gray-500 dark:text-gray-400 mb-1">
                  Сумма
                </label>
                <div className="relative">
                  <input
                    type="text"
                    id="investmentAmount"
                    value={newInvestment.amount}
                    onChange={(e) => setNewInvestment({ ...newInvestment, amount: e.target.value })}
                    className="form-input text-sm w-full"
                    placeholder="Сумма инвестиции"
                  />
                  <span className="absolute inset-y-0 right-3 flex items-center text-gray-500 pointer-events-none">
                    ₽
                  </span>
                </div>
              </div>
              <div className="col-span-2 flex items-end">
                <Button
                  type="button"
                  onClick={handleAddInvestment}
                  variant="outline"
                  size="sm"
                  className="w-full h-9"
                  icon={<PlusCircle className="w-4 h-4" />}
                />
              </div>
            </div>

            {/* Список добавленных инвестиций */}
            {formData.additionalInvestments && formData.additionalInvestments.length > 0 && (
              <div className="mt-2 overflow-auto max-h-40 bg-white dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-600">
                  <thead className="bg-gray-50 dark:bg-gray-800">
                    <tr>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Месяц
                      </th>
                      <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Сумма
                      </th>
                      <th className="px-3 py-2 w-10"></th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-gray-700 divide-y divide-gray-200 dark:divide-gray-600">
                    {formData.additionalInvestments.map((investment, index) => (
                      <tr key={index} className="hover:bg-gray-50 dark:hover:bg-gray-600">
                        <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900 dark:text-gray-300">
                          {investment.month}
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap text-sm text-right font-medium text-green-600 dark:text-green-400">
                          +{new Intl.NumberFormat('ru-RU').format(investment.amount)} ₽
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap text-right text-sm">
                          <button
                            type="button"
                            onClick={() => handleRemoveInvestment(index)}
                            className="text-red-500 hover:text-red-700 dark:hover:text-red-300"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Снятия */}
          <div>
            <h4 className="text-sm font-medium text-gray-800 dark:text-gray-200 mb-2 flex items-center">
              <ArrowDownRight className="w-4 h-4 mr-1 text-red-500" />
              Снятия средств
            </h4>

            <div className="grid grid-cols-12 gap-3 mb-2">
              <div className="col-span-4">
                <label htmlFor="withdrawalMonth" className="block text-xs text-gray-500 dark:text-gray-400 mb-1">
                  Месяц
                </label>
                <input
                  type="text"
                  id="withdrawalMonth"
                  value={newWithdrawal.month}
                  onChange={(e) => setNewWithdrawal({ ...newWithdrawal, month: e.target.value })}
                  className="form-input text-sm w-full"
                  placeholder="Номер месяца"
                />
              </div>
              <div className="col-span-6">
                <label htmlFor="withdrawalAmount" className="block text-xs text-gray-500 dark:text-gray-400 mb-1">
                  Сумма
                </label>
                <div className="relative">
                  <input
                    type="text"
                    id="withdrawalAmount"
                    value={newWithdrawal.amount}
                    onChange={(e) => setNewWithdrawal({ ...newWithdrawal, amount: e.target.value })}
                    className="form-input text-sm w-full"
                    placeholder="Сумма снятия"
                  />
                  <span className="absolute inset-y-0 right-3 flex items-center text-gray-500 pointer-events-none">
                    ₽
                  </span>
                </div>
              </div>
              <div className="col-span-2 flex items-end">
                <Button
                  type="button"
                  onClick={handleAddWithdrawal}
                  variant="outline"
                  size="sm"
                  className="w-full h-9"
                  icon={<PlusCircle className="w-4 h-4" />}
                />
              </div>
            </div>

            {/* Список добавленных снятий */}
            {formData.withdrawals && formData.withdrawals.length > 0 && (
              <div className="mt-2 overflow-auto max-h-40 bg-white dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-600">
                  <thead className="bg-gray-50 dark:bg-gray-800">
                    <tr>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Месяц
                      </th>
                      <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Сумма
                      </th>
                      <th className="px-3 py-2 w-10"></th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-gray-700 divide-y divide-gray-200 dark:divide-gray-600">
                    {formData.withdrawals.map((withdrawal, index) => (
                      <tr key={index} className="hover:bg-gray-50 dark:hover:bg-gray-600">
                        <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900 dark:text-gray-300">
                          {withdrawal.month}
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap text-sm text-right font-medium text-red-600 dark:text-red-400">
                          -{new Intl.NumberFormat('ru-RU').format(withdrawal.amount)} ₽
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap text-right text-sm">
                          <button
                            type="button"
                            onClick={() => handleRemoveWithdrawal(index)}
                            className="text-red-500 hover:text-red-700 dark:hover:text-red-300"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <div className="p-3 bg-purple-100 dark:bg-purple-900/30 rounded-lg text-sm text-purple-700 dark:text-purple-300">
            <p>
              <strong>Примечание:</strong> Добавьте крупные разовые пополнения или снятия средств для более точного планирования.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default CashFlowSection;